import React, {FC} from 'react';

import {VisualCellActivityTypesEnum} from '../../../../../libs/inboxes/business/src/cells/cellActivity';
import {FrontClickEvent} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {MessageTypesEnum} from '../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {
  isConversationInTeamContextOrSelfAssigned,
  isInternalConversation,
  UnreadStatusesEnum,
} from '../../../../helpers/models/conversationHelpers';
import {findMostRecentActivityForCell} from '../content/cell/cellActivityCommon';
import {CellDraft} from '../content/cell/cellDraft';
import {CellHoverIconsContainer} from '../content/cell/cellHoverIconsContainer';
import {CellInboxColor} from '../content/cell/cellInboxColor';
import {renderCellSubject, renderCellSubjectContent} from '../content/cell/cellMiddle';
import {CellOriginalLinkedIcon} from '../content/cell/cellOriginalLinkedIcon';
import {CellOverlay} from '../content/cell/cellOverlay';
import {CellRecipient} from '../content/cell/cellRecipient';
import {CellRecipientSenders} from '../content/cell/cellRecipientSenders';
import {CellTags} from '../content/cell/cellTags';
import {CompactCellActivity} from '../content/cell/compactCellActivity';
import {ConversationCellCommonProps} from '../conversationCellCommon';
import {useMaximumNumberOfCellIndicators} from './cellTagsCommon';
import {CompactCellComponents} from './compactCellContentCommon';

/*
 * Props.
 */

interface CompactConversationCellContentProps extends ConversationCellCommonProps {
  onToggleMarkAsRead: () => void;
  /** Whether this particular cell will display quick actions when the cell is hovered. */
  shouldShowQuickActionsOnCellHover: boolean;
  onSnoozeClick: (event: FrontClickEvent) => void;
  isSnoozeDropdownOpen: boolean;
  onSnoozeDropdownClose: () => void;
  onOpenClick: (event: FrontClickEvent) => void;
  onTrashClick: (event: FrontClickEvent) => void;
}

/*
 * Style.
 */

/*
 * Component.
 */

export const CompactConversationCellContent: FC<CompactConversationCellContentProps> = (props) => {
  const {
    shouldRenderConversationSenders,
    userId,
    conversationSenders,
    conversationHighlight,
    draft,
    conversation,
    numMessages,
    recipient,
    isSelected,
    externalChannelType,
    applicationChannelType,
    inbox,
    shouldShowQuickActionsOnCellHover,
    cellsContext,
    onTrashClick,
    onOpenClick,
    reminderDate,
    onSnoozeClick,
    isSnoozeDropdownOpen,
    onSnoozeDropdownClose,
    shouldConsiderInPrivateTag,
    overlay,
    isStarred,
    isOriginalConversation,
  } = props;
  const {messageType} = conversation;

  const conversationMessageType = (numMessages === 0 && draft && draft.type) || messageType;
  const draftSubject = draft && draft.subject;
  const subject = draftSubject || conversation.subject || '';
  const {maxIndicators, indicatorsWrapperColumns} = useMaximumNumberOfCellIndicators({
    highlight: conversationHighlight,
    numberOfTags: props.tags.length,
    isStarred: Boolean(isStarred),
    isOriginalConversation: Boolean(isOriginalConversation),
    isCompact: true,
  });

  const tagsRender = shouldRenderIndicators(props) ? (
    <>
      {isOriginalConversation && (
        <CellOriginalLinkedIcon
          isSelected={isSelected}
          numLinkedConversations={conversation.numLinkedConversations}
        />
      )}
      <CellTags
        maxTagsToShow={maxIndicators}
        isSelected={isSelected}
        tags={props.tags}
        isStarred={isStarred}
      />
    </>
  ) : null;

  const mostRecentActivity = findMostRecentActivityForCell({
    lastManualMessage: props.lastManualMessage,
    lastComment: props.lastComment,
    lastCommentDraft: props.lastCommentDraft,
    draft: props.draft,
    commentDraftSummary: props.commentDraftSummary,
    isInDraftsFolder: false,
    teammateId: props.cellsContext.teammateId,
  });

  return (
    <>
      <CompactCellComponents.FirstThird>
        <CompactCellComponents.CellStatus
          status={props.status}
          unreadStatus={props.unreadStatus}
          isSelected={props.isSelected}
          toggleMarkAsRead={props.onToggleMarkAsRead}
        />
        <CompactCellComponents.Recipient>
          {shouldRenderConversationSenders && conversationSenders ? (
            <CellRecipientSenders
              conversation={props.conversation}
              conversationSenders={conversationSenders}
              userId={userId}
              assignee={props.assignee}
              isSelected={props.isSelected}
              isUnread={props.unreadStatus !== UnreadStatusesEnum.NONE}
              isDiscussion={props.conversation.messageType === MessageTypesEnum.INTERNAL}
              trackerTeammates={props.trackerTeammates}
              isUserTracking={isDefined(props.trackerStatus)}
              conversationHighlight={props.conversationHighlight}
            />
          ) : (
            <CellRecipient
              conversation={props.conversation}
              recipientHighlight={conversationHighlight && conversationHighlight.recipient}
              trackerTeammates={props.trackerTeammates}
              userId={userId}
              recipient={props.recipient}
              assignee={props.assignee}
              inbox={props.inbox}
              lastCommentAuthor={props.lastComment?.author}
              isSelected={props.isSelected}
              isUnread={props.unreadStatus !== UnreadStatusesEnum.NONE}
            />
          )}
        </CompactCellComponents.Recipient>
      </CompactCellComponents.FirstThird>
      <CompactCellComponents.MiddleThird $hasTags={Boolean(tagsRender)}>
        <CompactCellComponents.ContentWrapper>
          <CompactCellComponents.SubjectWrapper
            $isSearchSummaryMatch={Boolean(
              conversationHighlight?.attachments || conversationHighlight?.summary,
            )}
          >
            {mostRecentActivity?.type === VisualCellActivityTypesEnum.DRAFT_MESSAGE && (
              <CompactCellComponents.CellDraftWrapper>
                <CellDraft
                  isInDraftsFolder={false}
                  hasAutoShareDrafts={props.hasAutoShareDrafts}
                  draft={mostRecentActivity.draft}
                  isSelected={isSelected}
                  conversationId={props.conversation.id}
                />
              </CompactCellComponents.CellDraftWrapper>
            )}
            <TooltipCoordinator
              render={() =>
                renderCellSubjectContent({
                  messageType: conversationMessageType,
                  subject,
                  subjectHighlight: undefined,
                  recipient,
                  isSelected,
                  externalChannelType,
                  applicationChannelType,
                  isCompact: false,
                })
              }
            >
              {renderCellSubject({
                messageType: conversationMessageType,
                subject,
                subjectHighlight: conversationHighlight && conversationHighlight.subject,
                recipient,
                isSelected,
                externalChannelType,
                applicationChannelType,
                isCompact: true,
                hasTooltip: true,
              })}
            </TooltipCoordinator>
          </CompactCellComponents.SubjectWrapper>
          <CompactCellActivity
            conversationId={props.conversation.id}
            mentionableAliases={props.mentionableAliases}
            summary={props.conversation.summary}
            conversationHighlight={props.conversationHighlight}
            searchActivity={props.searchActivity}
            commentingTeammate={props.commentingTeammate}
            replyingTeammate={props.replyingTeammate}
            draftSeedAuthors={props.draftSeedAuthors}
            lastManualMessage={props.lastManualMessage}
            lastComment={props.lastComment}
            lastCommentDraft={props.lastCommentDraft}
            commentDraftSummary={props.commentDraftSummary}
            isSelected={props.isSelected}
            draft={props.draft}
            bumpActivity={props.bumpActivity}
            bumpComment={props.bumpComment}
            bumpTeammateSource={props.bumpTeammateSource}
            unreadStatus={props.unreadStatus}
            searchResultCommentSummary={props.searchResultCommentSummary}
            hasAutoShareDrafts={props.hasAutoShareDrafts}
            isInDraftsFolder={false}
            teammateId={props.cellsContext.teammateId}
          />
        </CompactCellComponents.ContentWrapper>
        {tagsRender && (
          <CompactCellComponents.TagsWrapper
            $columns={indicatorsWrapperColumns}
            $isInSearch={Boolean(conversationHighlight)}
          >
            {tagsRender}
          </CompactCellComponents.TagsWrapper>
        )}
      </CompactCellComponents.MiddleThird>
      <CompactCellComponents.LastThird>
        {conversation.hasAttachments && <CompactCellComponents.AttachmentRightIcon isSelected={isSelected} />}
        {isInternalConversation(conversation) && (
          <CompactCellComponents.InternalIcon isSelected={isSelected} />
        )}
        <CompactCellComponents.BadgeValue
          isSelected={props.isSelected}
          badgeValue={props.numMessages ?? 0}
          isCompact
        />
        <CompactCellComponents.CellDate
          isCompact
          date={props.date}
          reminderDate={props.reminderDate}
          slaTimerDate={props.slaTimerDate}
          waitingSinceMs={conversation.orders.waiting_since ?? 0}
          isSelected={isSelected}
        />
        <CompactCellComponents.StatusIcon
          isSelected={isSelected}
          unreadStatus={props.unreadStatus}
          status={props.status}
          reminderDate={props.reminderDate}
        />
        {shouldShowQuickActionsOnCellHover && (
          <CellHoverIconsContainer
            cellsContext={cellsContext}
            onTrashClick={onTrashClick}
            onOpenClick={onOpenClick}
            isSelected={isSelected}
            status={props.status}
            conversation={conversation}
            hasReminder={Boolean(reminderDate)}
            isTeamContext={isConversationInTeamContextOrSelfAssigned(conversation, cellsContext)}
            isDelegated={cellsContext.isDelegation}
            onSnoozeClick={onSnoozeClick}
            isSnoozeDropdownOpen={isSnoozeDropdownOpen}
            onSnoozeDropdownClose={onSnoozeDropdownClose}
            shouldConsiderInPrivateTag={Boolean(shouldConsiderInPrivateTag)}
            isCompact
          />
        )}
      </CompactCellComponents.LastThird>
      {inbox && <CellInboxColor inbox={inbox} size={12} />}
      {overlay && (
        <CellOverlay
          isSelected={isSelected}
          type={overlay.type}
          requester={overlay.requester}
          assignee={overlay.assignee}
          inbox={overlay.inbox}
          conversationId={overlay.conversationId}
          isHorizontal
        />
      )}
    </>
  );
};

function shouldRenderIndicators(props: CompactConversationCellContentProps) {
  let indicatorsLength = props.tags.length;
  if (props.isOriginalConversation) {
    indicatorsLength++;
  }
  if (props.isStarred) {
    indicatorsLength++;
  }

  return Boolean(indicatorsLength);
}
